import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Scroll reveal
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    root.querySelectorAll('.reveal').forEach(el => io.observe(el));

    // Header scroll
    const h = root.querySelector('.lp-header');
    const onScroll = () => h?.classList.toggle('scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={rootRef} className="lp-root">

      {/* ── Header ── */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <img src="/nico-1.png" alt="NICO" className="lp-logo" />
          <nav className="lp-nav">
            <a href="/para-oficinas" onClick={e => { e.preventDefault(); navigate('/para-oficinas'); }}>Para oficinas e frotas</a>
            <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }} className="lp-nav-link">Entrar</a>
            <button className="lp-nav-cta" onClick={() => navigate('/login?tab=register')}>Criar conta grátis</button>
          </nav>
          <button className="lp-mobile-toggle" onClick={() => {
            const nav = rootRef.current?.querySelector('.lp-nav');
            nav?.classList.toggle('open');
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14562e" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-text">
            <span className="lp-pill reveal">Grátis até 3 veículos, para sempre</span>
            <h1 className="reveal">Toda a história do seu carro, sempre com você</h1>
            <p className="reveal">Cadastre seu veículo, registre as manutenções e receba lembretes de quando refazer cada serviço. Sem planilha, sem papelada, sem esquecimento.</p>
            <div className="lp-hero-btns reveal">
              <button className="lp-btn-primary" onClick={() => navigate('/login?tab=register')}>Criar minha conta grátis →</button>
              <button className="lp-btn-outline" onClick={() => navigate('/login')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>
                Já tenho conta
              </button>
            </div>
          </div>
          <div className="lp-hero-visual reveal">
            <img src="/devices-mockup.png" alt="NICO — App de manutenção veicular" className="lp-hero-mockup" />
            <div className="lp-float-card lp-float-odometer">
              <span className="lp-float-label">Próx. revisão</span>
              <span className="lp-float-value">em 2.500 km</span>
            </div>
            <div className="lp-float-card lp-float-service">
              <span className="lp-float-icon">✓</span>
              <div>
                <span className="lp-float-label">Serviço validado</span>
                <span className="lp-float-desc">Filtro de combustível</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-section lp-features">
        <div className="lp-container">
          <h2 className="lp-section-title reveal">O que você tem de graça no Nico</h2>
          <div className="lp-features-grid">
            {[
              { icon: '📋', title: 'Histórico completo', desc: 'Cada serviço registrado fica salvo com data, oficina e valor.' },
              { icon: '🔔', title: 'Lembretes automáticos', desc: 'O Nico avisa quando é hora da próxima revisão, óleo ou pneu.' },
              { icon: '🚗', title: 'Vários veículos', desc: 'Carro, moto ou elétrico: sua garagem inteira organizada.' },
              { icon: '💰', title: 'Gastos sob controle', desc: 'Veja quanto seu veículo custou por mês e por tipo de serviço.' },
            ].map((f, i) => (
              <div key={i} className="lp-feature-card reveal">
                <div className="lp-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works + Plan ── */}
      <section className="lp-section lp-how-plan">
        <div className="lp-container">
          <div className="lp-how-plan-grid">
            {/* Como funciona */}
            <div className="lp-how-card reveal">
              <h2>Como funciona</h2>
              <div className="lp-steps">
                {[
                  { n: '1', title: 'Crie sua conta', desc: 'Só e-mail e placa. Leva menos de um minuto.' },
                  { n: '2', title: 'Registre o que fizer', desc: 'Ou deixe a oficina parceira registrar por você.' },
                  { n: '3', title: 'Receba os lembretes', desc: 'O Nico cuida das datas e você só dirige.' },
                ].map((s, i) => (
                  <div key={i} className="lp-step">
                    <span className="lp-step-n">{s.n}</span>
                    <div>
                      <strong>{s.title}</strong>
                      <p>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="lp-how-footer">
                <img src="/nico-dog-face.png" alt="" className="lp-how-nico" />
                <span>Todo serviço feito por oficina parceira entra automaticamente no histórico.</span>
              </div>
            </div>

            {/* Plano do motorista */}
            <div className="lp-plan-card reveal">
              <span className="lp-plan-badge">Grátis</span>
              <h2>Plano do motorista</h2>
              <p className="lp-plan-desc">Tudo que você precisa para cuidar do seu veículo, sem custo.</p>
              <ul className="lp-plan-list">
                <li>Cadastro de veículos ilimitado</li>
                <li>Registro de manutenções e notas</li>
                <li>Alertas de revisão e licenciamento</li>
                <li>Serviços do carro reunidos no app</li>
              </ul>
              <button className="lp-btn-primary lp-btn-full" onClick={() => navigate('/login?tab=register')}>Começar agora</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Mecânico ── */}
      <section className="lp-section lp-mechanic-cta">
        <div className="lp-container">
          <div className="lp-mechanic-inner reveal">
            <div>
              <span className="lp-eyebrow">🔧 Para mecânicos</span>
              <h2>É mecânico? A área do Nico para oficinas é gratuita</h2>
              <p>Registre os serviços que fizer, envie o histórico para o cliente e apareça na busca. Página simples, sem mensalidade.</p>
            </div>
            <div className="lp-mechanic-btns">
              <button className="lp-btn-primary" onClick={() => navigate('/login?tab=register')}>Criar conta de oficina grátis</button>
              <button className="lp-btn-ghost" onClick={() => navigate('/para-oficinas')}>Ver planos avançados →</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section lp-faq">
        <div className="lp-container">
          <h2 className="lp-section-title reveal">Perguntas frequentes</h2>
          <div className="lp-faq-grid">
            {[
              { q: 'O NICO é realmente grátis?', a: 'Sim. O plano do motorista é 100% gratuito, sem cartão de crédito. Você pode cadastrar até 3 veículos, registrar serviços ilimitados e receber alertas de manutenção sem pagar nada.' },
              { q: 'Funciona para moto, caminhonete e elétrico?', a: 'Sim. O NICO funciona para qualquer veículo: carro, moto, pickup, SUV, van e elétrico. Todos os tipos são suportados pela tabela FIPE.' },
              { q: 'Como funciona a validação pelo mecânico?', a: 'Quando você registra um serviço e indica o mecânico, ele recebe uma solicitação para confirmar. O serviço validado ganha um selo de verificação, criando um histórico confiável para revenda.' },
              { q: 'Meus dados ficam seguros?', a: 'Sim. Seus dados são protegidos conforme a LGPD. Usamos criptografia, senhas com hash seguro e seus dados nunca são compartilhados com terceiros sem sua autorização.' },
              { q: 'Posso usar o NICO para vender meu carro?', a: 'Sim. O histórico verificado do NICO funciona como um selo de confiança. Você pode gerar um relatório completo com todos os serviços registrados para mostrar ao comprador.' },
              { q: 'Preciso instalar algum app?', a: 'Não. O NICO funciona direto no navegador do celular ou computador. Basta acessar usenico.com.br e criar sua conta.' },
            ].map((item, i) => (
              <div key={i} className="lp-faq-item reveal" onClick={e => e.currentTarget.classList.toggle('open')}>
                <button className="lp-faq-q" type="button">
                  {item.q}
                  <span className="lp-faq-arrow">▼</span>
                </button>
                <p className="lp-faq-a">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div className="lp-footer-brand">
            <img src="/nico-2.png" alt="NICO" />
            <p>A garagem digital do Brasil.<br/>Histórico veicular para quem cuida do seu carro.</p>
          </div>
          <div className="lp-footer-col">
            <h4>Produto</h4>
            <a href="/login?tab=register" onClick={e => { e.preventDefault(); navigate('/login?tab=register'); }}>Criar conta</a>
            <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }}>Entrar</a>
            <a href="/para-oficinas" onClick={e => { e.preventDefault(); navigate('/para-oficinas'); }}>Para oficinas</a>
          </div>
          <div className="lp-footer-col">
            <h4>Legal</h4>
            <a href="/termos" onClick={e => { e.preventDefault(); navigate('/termos'); }}>Termos de uso</a>
            <a href="/privacidade" onClick={e => { e.preventDefault(); navigate('/privacidade'); }}>Política de privacidade</a>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} LAB2513 Tech & Design Studio. Todos os direitos reservados.</span>
          <span>usenico.com.br</span>
        </div>
      </footer>
    </div>
  );
}
